import { IconCamera, IconCloudUpload, IconPhotoPlus } from '@tabler/icons-react';
import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, Spinner, useToast } from '@/components/common';
import { logger } from '@/lib/logger';
import type { Xray } from '@/types/api';
import { parseTeethList } from '@/utils/dental';
import { type CompressedImage, compressXray } from '@/utils/image';
import { formatDate } from './format';
import { XrayImage } from './XrayImage';
import { XrayLightbox } from './XrayLightbox';

/** Phones and tablets get a camera button; on a desktop it would only open the file picker again. */
const hasCamera = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** Previews of the chosen images and the teeth they show, before they are saved. */
function NewXraySheet({
  images,
  onSave,
  onClose,
}: {
  images: CompressedImage[];
  onSave: (teeth: number[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [teethText, setTeethText] = useState('');
  const teeth = parseTeethList(teethText);
  return (
    <Sheet title={t('chart.xrays.newTitle', { count: images.length })} onClose={onClose}>
      <div className="stack">
        <ul className="xray-grid">
          {images.map((image, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the list never changes while the sheet is open
            <li key={i}>
              <XrayImage
                source={String(i)}
                load={() => Promise.resolve(image.blob)}
                alt={t('chart.xrays.preview')}
              />
            </li>
          ))}
        </ul>
        <label className="field">
          <span className="field__label">{t('chart.xrays.teeth')}</span>
          <input
            className="input"
            inputMode="numeric"
            value={teethText}
            placeholder={t('chart.xrays.teethHint')}
            aria-invalid={teeth === null}
            onChange={(e) => setTeethText(e.target.value)}
          />
          {teeth === null && (
            <span className="small error-text">{t('chart.xrays.teethInvalid')}</span>
          )}
        </label>
        <p className="small muted">
          {t('chart.xrays.size', {
            size: Math.round(images.reduce((sum, image) => sum + image.blob.size, 0) / 1024),
          })}
        </p>
        <button
          type="button"
          className="btn btn--dark btn--block"
          disabled={teeth === null}
          onClick={() => teeth && onSave(teeth)}
        >
          {t('common.save')}
        </button>
      </div>
    </Sheet>
  );
}

type Props = {
  xrays: Xray[];
  /** Images taken now go to the open visit of the day (null) or to this earlier visit. */
  visitId: string | null;
  onAdd: (images: CompressedImage[], teeth: number[], visitId: string | null) => Promise<void>;
  onDelete: (xray: Xray) => Promise<void>;
  image: (xray: Xray) => Promise<Blob>;
};

/** X-ray images: taken with the camera or picked from files, shrunk on the device. */
export function XrayCard({ xrays, visitId, onAdd, onDelete, image }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const camera = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [draft, setDraft] = useState<CompressedImage[] | null>(null);
  // By clientId, not position: the list can re-sort while the lightbox is open.
  const [openId, setOpenId] = useState<string | null>(null);
  const openIndex = xrays.findIndex((x) => x.clientId === openId);

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = [...(e.target.files ?? [])];
    e.target.value = '';
    if (!picked.length) return;
    setProcessing(true);
    const images: CompressedImage[] = [];
    for (const file of picked) {
      try {
        images.push(await compressXray(file));
      } catch (err) {
        logger.warn('xray: could not read image', err);
        toast.show(t('chart.xrays.notImage', { name: file.name }), 'error');
      }
    }
    setProcessing(false);
    if (images.length) setDraft(images);
  };

  const label = (x: Xray) =>
    [formatDate(x.takenAt), x.teeth.length ? x.teeth.join(', ') : null].filter(Boolean).join(' · ');

  return (
    <section className="card xray-card">
      <div className="card__head">
        <h2>{t('chart.xrays.title')}</h2>
        {hasCamera() && (
          <button
            type="button"
            className="btn btn--sm"
            disabled={processing}
            onClick={() => camera.current?.click()}
          >
            <IconCamera size={18} aria-hidden="true" />
            {t('chart.xrays.camera')}
          </button>
        )}
        <button
          type="button"
          className="btn btn--sm"
          disabled={processing}
          onClick={() => files.current?.click()}
        >
          <IconPhotoPlus size={18} aria-hidden="true" />
          {t('chart.xrays.add')}
        </button>
        <input
          ref={camera}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={choose}
        />
        <input ref={files} type="file" accept="image/*" multiple hidden onChange={choose} />
      </div>

      {processing && <Spinner label={t('chart.xrays.processing')} />}
      {xrays.length ? (
        <ul className="xray-grid">
          {xrays.map((x) => (
            <li key={x.clientId}>
              <button
                type="button"
                className="xray-thumb"
                onClick={() => setOpenId(x.clientId)}
                aria-label={t('chart.xrays.open', { label: label(x) })}
              >
                <XrayImage source={x.id} load={() => image(x)} alt="" />
                <span className="xray-thumb__label">{label(x)}</span>
                {x.local && (
                  <span
                    className={`badge ${x.failed ? 'badge--rejected' : 'badge--pending'} xray-thumb__badge`}
                  >
                    {!x.failed && <IconCloudUpload size={14} aria-hidden="true" />}
                    {x.failed ? t('chart.xrays.failed') : t('chart.xrays.pending')}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        !processing && <p className="small muted">{t('chart.xrays.none')}</p>
      )}

      {draft && (
        <NewXraySheet
          images={draft}
          onClose={() => setDraft(null)}
          onSave={(teeth) => {
            setDraft(null);
            void onAdd(draft, teeth, visitId);
          }}
        />
      )}
      {openIndex >= 0 && (
        <XrayLightbox
          xrays={xrays}
          index={openIndex}
          label={label}
          image={image}
          onIndex={(i) => setOpenId(xrays[i]?.clientId ?? null)}
          onClose={() => setOpenId(null)}
          onDelete={async (x) => {
            await onDelete(x);
            setOpenId(null);
          }}
        />
      )}
    </section>
  );
}
